import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import { ArrowDown, Loader2 } from 'lucide-react';

import Sheet from '@/components/Sheet';
import Composer from '@/components/chat/Composer';
import MessageBubble from '@/components/chat/MessageBubble';
import PhotoViewer from '@/components/chat/PhotoViewer';
import PushHint from '@/components/chat/PushHint';
import { replyOf } from '@/components/chat/ReplyQuote';
import { apiDelete, apiErrorMessage, apiGet, apiPost } from '@/lib/api';
import {
  markDeleted,
  onChatDelete,
  onChatHide,
  onChatMessage,
  setChatPresence,
  useChatStore,
  type ChatMessage,
} from '@/lib/chat';
import { useAuthStore } from '@/store/auth.store';

interface ChatPage {
  items: ChatMessage[];
  hasMore: boolean;
  readAt: string;
}

const PAGE = 50;
const GROUP_GAP_MS = 5 * 60 * 1000;
/** Протянул ленту вниз на столько — клавиатура прячется, как в мессенджере. */
const DISMISS_DRAG_PX = 28;
/** Тап по цитате старого сообщения: догружаем историю, но не бесконечно. */
const JUMP_MAX_PAGES = 10;
const HIGHLIGHT_MS = 1600;

const MSK_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' });
const DAY_TITLE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'Europe/Moscow' });

function dayTitle(iso: string): string {
  const key = MSK_DAY.format(new Date(iso));
  if (key === MSK_DAY.format(new Date())) return 'Сегодня';
  if (key === MSK_DAY.format(new Date(Date.now() - 86_400_000))) return 'Вчера';
  return DAY_TITLE.format(new Date(iso));
}

function mergeMessages(current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const seen = new Set(current.map((m) => m.id));
  const fresh = incoming.filter((m) => !seen.has(m.id));
  if (fresh.length === 0) return current;
  return [...current, ...fresh].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export default function Chat() {
  const me = useAuthStore((s) => s.manager);
  const setUnread = useChatStore((s) => s.setUnread);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newBelow, setNewBelow] = useState(0);
  const [photo, setPhoto] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ChatMessage | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottom = useRef(true);
  const restoreFrom = useRef<number | null>(null);
  const readTimer = useRef<number | null>(null);
  const highlightTimer = useRef<number | null>(null);
  const touchY = useRef<number | null>(null);
  // Прыжок к цитате догружает страницы в цикле — ему нужна лента после каждой.
  const messagesRef = useRef(messages);
  const hasMoreRef = useRef(hasMore);
  const olderBusy = useRef(false);

  useLayoutEffect(() => {
    messagesRef.current = messages;
    hasMoreRef.current = hasMore;
  });

  const markRead = useCallback(() => {
    if (readTimer.current) window.clearTimeout(readTimer.current);
    readTimer.current = window.setTimeout(() => {
      setUnread(0);
      void apiPost('/chat/read', undefined, { silent: true }).catch(() => undefined);
    }, 400);
  }, [setUnread]);

  // Лента и присутствие: пока чат открыт и виден, пуши этому менеджеру не идут.
  useEffect(() => {
    let cancelled = false;
    setChatPresence(true);
    void apiGet<ChatPage>(`/chat?limit=${PAGE}`, { silent: true })
      .then((page) => {
        if (cancelled) return;
        setMessages(page.items);
        setHasMore(page.hasMore);
        markRead();
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Не удалось загрузить чат'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      setChatPresence(visible);
      if (visible) markRead();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const offMessage = onChatMessage((m) => {
      setMessages((cur) => mergeMessages(cur, [m]));
      if (document.visibilityState === 'visible') markRead();
      if (!stickToBottom.current && m.author.id !== useAuthStore.getState().manager?.id) {
        setNewBelow((n) => n + 1);
      }
    });
    // Удалено у всех — на месте сообщения плашка; удалено у меня — пропадает.
    const offDelete = onChatDelete((id) => {
      setMessages((cur) => markDeleted(cur, id));
      setReplyToId((cur) => (cur === id ? null : cur));
    });
    const offHide = onChatHide((id) => {
      setMessages((cur) => cur.filter((m) => m.id !== id));
      setReplyToId((cur) => (cur === id ? null : cur));
    });

    return () => {
      cancelled = true;
      setChatPresence(false);
      document.removeEventListener('visibilitychange', onVisibility);
      offMessage();
      offDelete();
      offHide();
      if (readTimer.current) window.clearTimeout(readTimer.current);
      if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
    };
  }, [markRead]);

  // Прокрутка: подгрузили историю — держим место; пришло новое у низа — едем вниз.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (restoreFrom.current !== null) {
      el.scrollTop = el.scrollHeight - restoreFrom.current;
      restoreFrom.current = null;
      return;
    }
    if (stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Клавиатура открылась и лента стала ниже — последние сообщения остаются на виду.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stickToBottom.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottom.current = atBottom;
    if (atBottom) setNewBelow(0);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = true;
    setNewBelow(0);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  /** Страница истории раньше первой в ленте. true — загрузили. */
  const loadOlder = async (): Promise<boolean> => {
    const first = messagesRef.current[0];
    if (!first || olderBusy.current) return false;
    olderBusy.current = true;
    setLoadingOlder(true);
    try {
      const page = await apiGet<ChatPage>(`/chat?limit=${PAGE}&before=${first.id}`, { silent: true });
      const el = scrollRef.current;
      restoreFrom.current = el ? el.scrollHeight - el.scrollTop : null;
      // Синхронно: прыжок к цитате ищет сообщение в DOM сразу после загрузки.
      flushSync(() => {
        setMessages((cur) => mergeMessages(cur, page.items));
        setHasMore(page.hasMore);
      });
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось загрузить историю'));
      return false;
    } finally {
      olderBusy.current = false;
      setLoadingOlder(false);
    }
  };

  const flash = (id: string) => {
    setHighlightId(id);
    if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
  };

  /** Тап по цитате: к исходному сообщению, догружая историю, если оно выше. */
  const jumpTo = async (id: string) => {
    for (let page = 0; ; page++) {
      const el = scrollRef.current?.querySelector<HTMLElement>(`[data-msg-id="${CSS.escape(id)}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        flash(id);
        return;
      }
      if (page >= JUMP_MAX_PAGES || !hasMoreRef.current || !(await loadOlder())) break;
    }
    toast('Исходное сообщение не найдено');
  };

  const startReply = (m: ChatMessage) => {
    setReplyToId(m.id);
    // В том же жесте, что свайп или клик, — иначе iOS не откроет клавиатуру.
    inputRef.current?.focus();
  };

  const onSent = (msg: ChatMessage, replyId: string | null) => {
    stickToBottom.current = true;
    setMessages((cur) => mergeMessages(cur, [msg]));
    if (replyId) setReplyToId((cur) => (cur === replyId ? null : cur));
  };

  const deleteForAll = async (m: ChatMessage) => {
    setDeleteBusy(true);
    try {
      await apiDelete(`/chat/${m.id}`, { silent: true });
      setMessages((cur) => markDeleted(cur, m.id));
      setReplyToId((cur) => (cur === m.id ? null : cur));
      setDeleting(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось удалить сообщение'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const deleteForMe = async (m: ChatMessage) => {
    setDeleteBusy(true);
    try {
      await apiPost(`/chat/${m.id}/hide`, undefined, { silent: true });
      setMessages((cur) => cur.filter((x) => x.id !== m.id));
      setReplyToId((cur) => (cur === m.id ? null : cur));
      setDeleting(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось удалить сообщение'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const replyMessage = replyToId ? (messages.find((m) => m.id === replyToId) ?? null) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PushHint />

      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          onTouchStart={(e) => {
            touchY.current = e.touches[0]?.clientY ?? null;
          }}
          onTouchMove={(e) => {
            const y = e.touches[0]?.clientY;
            const input = inputRef.current;
            if (touchY.current == null || y == null || !input || document.activeElement !== input) return;
            if (y - touchY.current > DISMISS_DRAG_PX) input.blur();
          }}
          className="absolute inset-0 overflow-y-auto overflow-x-hidden overscroll-contain"
        >
          <div className="mx-auto flex min-h-full max-w-3xl flex-col justify-end px-3 pb-3 pt-2 sm:px-5">
            {loading ? <div className="my-auto py-10 text-center text-sm text-ink/50">Загрузка…</div> : null}
            {error ? <div className="my-auto py-10 text-center text-sm text-ink/60">{error}</div> : null}
            {!loading && !error && messages.length === 0 ? (
              <div className="my-auto flex flex-col items-center gap-1 px-6 text-center">
                <div className="font-medium">Здесь пока тихо</div>
                <p className="text-sm text-ink/55">Напишите первым: план на неделю, новый клиент, вопрос команде.</p>
              </div>
            ) : null}

            {hasMore ? (
              <div className="mb-2 flex justify-center">
                <button
                  type="button"
                  className="btn-ghost !min-h-[36px] text-xs"
                  onClick={() => void loadOlder()}
                  disabled={loadingOlder}
                >
                  {loadingOlder ? <Loader2 size={14} className="animate-spin" aria-hidden /> : null}
                  Показать раньше
                </button>
              </div>
            ) : null}

            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const own = m.author.id != null && m.author.id === me?.id;
              const newDay = !prev || dayTitle(prev.createdAt) !== dayTitle(m.createdAt);
              const grouped =
                !newDay &&
                prev?.author.id === m.author.id &&
                new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_GAP_MS;
              return (
                <Fragment key={m.id}>
                  {newDay ? (
                    <div className="my-3 flex justify-center">
                      <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-ink/55 shadow-sm ring-1 ring-line/60">
                        {dayTitle(m.createdAt)}
                      </span>
                    </div>
                  ) : null}
                  <MessageBubble
                    message={m}
                    own={own}
                    grouped={grouped}
                    highlighted={highlightId === m.id}
                    onReply={m.deleted ? undefined : () => startReply(m)}
                    onDelete={own ? () => setDeleting(m) : undefined}
                    onOpenPhoto={setPhoto}
                    onOpenReply={(id) => void jumpTo(id)}
                  />
                </Fragment>
              );
            })}
          </div>
        </div>

        {newBelow > 0 ? (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-xs font-medium text-white shadow-card"
          >
            <ArrowDown size={14} aria-hidden /> Новые сообщения · {newBelow}
          </button>
        ) : null}
      </div>

      <Composer
        inputRef={inputRef}
        replyTo={replyMessage ? replyOf(replyMessage) : null}
        onCancelReply={() => setReplyToId(null)}
        onSent={onSent}
      />

      {photo ? <PhotoViewer url={photo} onClose={() => setPhoto(null)} /> : null}

      {deleting ? (
        <Sheet title="Удалить сообщение?" onClose={() => (deleteBusy ? undefined : setDeleting(null))}>
          <div className="flex flex-col gap-2">
            {!deleting.deleted ? (
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => void deleteForAll(deleting)}
                className="btn w-full bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
              >
                Удалить у всех
              </button>
            ) : null}
            <button
              type="button"
              disabled={deleteBusy}
              onClick={() => void deleteForMe(deleting)}
              className="btn w-full border border-line text-ink hover:bg-paper disabled:opacity-50"
            >
              Удалить у меня
            </button>
            <button type="button" disabled={deleteBusy} onClick={() => setDeleting(null)} className="btn-ghost w-full">
              Отмена
            </button>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-ink/50">
            {deleting.deleted
              ? 'Плашка пропадёт только из вашей ленты.'
              : '«У всех» — у участников на месте сообщения останется «Сообщение удалено». «У меня» — пропадёт только из вашей ленты.'}
          </p>
        </Sheet>
      ) : null}
    </div>
  );
}
